export const LABEL_WIDTH_PT = 144
export const LABEL_HEIGHT_PT = 72

const HORIZONTAL_PADDING_PT = 7.2
const VERTICAL_PADDING_PT = 4.32
const CONTENT_WIDTH_PT = LABEL_WIDTH_PT - (HORIZONTAL_PADDING_PT * 2)
const CONTENT_HEIGHT_PT = LABEL_HEIGHT_PT - (VERTICAL_PADDING_PT * 2)
const START_FONT_SIZE_PT = 28
const MIN_FONT_SIZE_PT = 7
const FONT_STEP_PT = 0.5
const LINE_HEIGHT_RATIO = 1.05

// Keep this in the same order as the Customs page's PRINT SHEET groups.
export const CUSTOMS_MEAL_CATEGORIES = [
  'chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2',
  'veg', 'cheat', 'pasta', 'salad', 'salad_2',
]

function isPlastic(value) {
  return /plastic/i.test(value || '')
}

export function buildCustomerLabelGroups(menu, customizations) {
  const dishByCategory = new Map(
    (menu?.dishes || []).filter((dish) => dish.category).map((dish) => [dish.category, dish])
  )
  const groupsByDish = new Map()
  let missingNameMeals = 0

  for (const customization of customizations || []) {
    for (const affectedMeal of customization?.affected_meals || []) {
      const parsedQuantity = Number(affectedMeal?.quantity)
      if (Number.isFinite(parsedQuantity) && parsedQuantity <= 0) continue

      const dish = dishByCategory.get(affectedMeal?.meal_type)
      if (!dish) continue // This is a Needs review entry, not a printable plate card.
      if (customization?.customer_name_missing) {
        missingNameMeals += Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1
        continue
      }

      if (!groupsByDish.has(dish.dish_id)) {
        groupsByDish.set(dish.dish_id, { dish, orders: [] })
      }
      groupsByDish.get(dish.dish_id).orders.push({
        name: customization?.customer_name == null || customization.customer_name === ''
          ? 'CUSTOMER NAME MISSING'
          : String(customization.customer_name),
        quantity: Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1,
        container: customization?.container_type || '',
      })
    }
  }

  const categoryIndex = (category) => {
    const index = CUSTOMS_MEAL_CATEGORIES.indexOf(category)
    return index === -1 ? CUSTOMS_MEAL_CATEGORIES.length : index
  }
  const groups = [...groupsByDish.values()]
    .sort((a, b) => categoryIndex(a.dish.category) - categoryIndex(b.dish.category))
    .map((group) => {
      // Stable sort matches Customs: GLASS cards first, then PLASTIC cards.
      group.orders.sort((a, b) => Number(isPlastic(a.container)) - Number(isPlastic(b.container)))
      const labels = group.orders.flatMap((order) =>
        Array.from({ length: order.quantity }, (_, copy) => ({
          name: order.name,
          copy: copy + 1,
          customerQuantity: order.quantity,
        }))
      )
      return { ...group, labels }
    })

  return {
    missingNameMeals,
    groups,
    labels: groups.flatMap((group) => group.labels),
    totalLabels: groups.reduce((sum, group) => sum + group.labels.length, 0),
  }
}

function wrapWholeWords(name, font, size) {
  const words = String(name).split(/\s+/).filter(Boolean)
  if (!words.length) return ['CUSTOMER NAME MISSING']

  const lines = []
  let current = ''
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word
    if (!current || font.widthOfTextAtSize(candidate, size) <= CONTENT_WIDTH_PT) {
      current = candidate
    } else {
      lines.push(current)
      current = word
    }
  }
  if (current) lines.push(current)
  return lines
}

export function fitCustomerName(name, font) {
  for (let size = START_FONT_SIZE_PT; size >= MIN_FONT_SIZE_PT; size -= FONT_STEP_PT) {
    const lines = wrapWholeWords(name, font, size)
    const tooWide = lines.some((line) => font.widthOfTextAtSize(line, size) > CONTENT_WIDTH_PT)
    const fontHeight = font.heightAtSize(size)
    const blockHeight = fontHeight + ((lines.length - 1) * size * LINE_HEIGHT_RATIO)
    if (!tooWide && blockHeight <= CONTENT_HEIGHT_PT) return { size, lines }
  }
  return { size: MIN_FONT_SIZE_PT, lines: wrapWholeWords(name, font, MIN_FONT_SIZE_PT) }
}

export async function createCustomerLabelsPdf(labels, fontBytes, title = 'Customer Labels') {
  // Keep the PDF renderer out of the main dashboard bundle; it is only needed when
  // an operator actually downloads labels.
  const [{ default: fontkit }, { PDFDocument, rgb }] = await Promise.all([
    import('@pdf-lib/fontkit'),
    import('pdf-lib'),
  ])
  const pdf = await PDFDocument.create()
  pdf.registerFontkit(fontkit)
  pdf.setTitle(title)
  pdf.setCreator('Havn Club Ops Dashboard')
  const font = await pdf.embedFont(fontBytes, { subset: true })

  for (const label of labels || []) {
    const page = pdf.addPage([LABEL_WIDTH_PT, LABEL_HEIGHT_PT])
    const { size, lines } = fitCustomerName(label.name, font)
    const lineHeight = size * LINE_HEIGHT_RATIO
    const fontHeight = font.heightAtSize(size)
    const ascentHeight = font.heightAtSize(size, { descender: false })
    const descenderDepth = Math.max(0, fontHeight - ascentHeight)
    const blockHeight = fontHeight + ((lines.length - 1) * lineHeight)
    const bottom = (LABEL_HEIGHT_PT - blockHeight) / 2

    lines.forEach((line, index) => {
      const width = font.widthOfTextAtSize(line, size)
      page.drawText(line, {
        x: (LABEL_WIDTH_PT - width) / 2,
        y: bottom + descenderDepth + ((lines.length - index - 1) * lineHeight),
        size,
        font,
        color: rgb(0, 0, 0),
      })
    })
  }

  return pdf.save()
}

export function customerLabelsFileName(group, index) {
  const safeName = String(group?.dish?.name || 'Customer-Labels')
    .trim()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
  return `${String(index + 1).padStart(2, '0')}_${safeName || 'Customer-Labels'}.pdf`
}
